<script lang="ts" setup>
import Button from "primevue/button"
import Popover from "primevue/popover"
import SrInputNumber from "./SrInputNumber.vue";
import FloatLabel from "primevue/floatlabel";
import Message from "primevue/message";
import z from "zod";
import { Form, FormField, type FormSubmitEvent } from "@primevue/forms";
import { useTemplateRef, computed, nextTick } from "vue";
import { zodResolver } from "@primevue/forms/resolvers/zod";
import isNull from "lodash/isNull";
import { useSpreadBellStore } from "@/stores/spreadBellStore";
import type { FormInstance } from "@primevue/forms";

const props = defineProps<{
  spreadLabel: string
  backSpreadLabel: string
  variant?: string
  severity?: string
  size?: "small" | "medium" | "large"
  rounded?: boolean
  class?: string | string[] | Record<string, any>
  label?: string
}>()

const bellStore = useSpreadBellStore()
const popover = useTemplateRef('po')
const settingsForm = useTemplateRef<FormInstance>('settingsForm')
const enabled = computed(() => !isNull(bellStore.spread) || !isNull(bellStore.backSpread))
const icon = computed(() => !enabled.value ? 'pi pi-bell-slash' : 'pi pi-bell')

const resolver = zodResolver(z.object({
  spread: z.union([
    z.number(),
    z.string().optional()
  ]),
  backSpread: z.union([
    z.number(),
    z.string().optional()
  ])
}))

const onFormSubmit = (e: FormSubmitEvent) => {
  if (e.valid) {
    bellStore.setSpread(spread.value)
    bellStore.setBackSpread(backSpread.value)
    toggle(e)
  } else {
    console.error(e.errors)
  }
}

const spread = computed(() => settingsForm.value?.states?.spread?.value)
const backSpread = computed(() => settingsForm.value?.states?.backSpread?.value)

const toggle = async (event: PointerEvent | FormSubmitEvent) => {
  if (popover.value) {
    popover.value.toggle(event as PointerEvent)
    await nextTick()
    settingsForm.value?.setFieldValue('spread', bellStore.spread)
    settingsForm.value?.setFieldValue('backSpread', bellStore.backSpread)
  }
}

const cleanForm = () => {
  bellStore.cleanState();
  settingsForm.value?.setFieldValue('spread', null)
  settingsForm.value?.setFieldValue('backSpread', null)
}
</script>

<template>
  <Button v-bind="$attrs" v-tooltip.bottom="$t('setBell')" :class="props.class" :icon="icon" @click="toggle"
    :size="size" :variant="props.variant ?? 'text'" :severity="props.severity" :rounded="props.rounded"
    :label="props.label" aria-label="Bell" />
  <Popover ref="po" class="w-70">
    <Message icon="pi pi-volume-up" size="small" severity="success" class="mb-3">{{ $t('bellInfo') }}</Message>
    <Form ref="settingsForm" @submit="onFormSubmit" :resolve="resolver" class="grid grid-cols-1 gap-3">
      <FormField name="spread">
        <FloatLabel variant="on">
          <SrInputNumber size="small" fluid :max-fraction-digits="2" :step="0.1" show-buttons>
          </SrInputNumber>
          <label>{{ props.spreadLabel }}</label>
        </FloatLabel>
      </FormField>
      <FormField name="backSpread">
        <FloatLabel variant="on">
          <SrInputNumber size="small" fluid :max-fraction-digits="2" :step="0.1" show-buttons></SrInputNumber>
          <label>{{ props.backSpreadLabel }}</label>
        </FloatLabel>
      </FormField>
      <Button severity="secondary" icon="pi pi-save" type="submit" size="small" :label="$t('save')"></Button>
      <Button v-if="enabled" severity="secondary" icon="pi pi-trash" type="button" @click="cleanForm" size="small"
        :label="$t('clean')"></Button>
    </Form>
  </Popover>
</template>
